import { useCallback, useState } from 'react';

/**
 * useState persisted to sessionStorage (survives navigation and reloads within
 * the tab, cleared when the session ends). Storage can be unavailable (private
 * mode, blocked site data): reads fall back to the initial value and writes are
 * best-effort, so the UI never depends on it.
 */
export function useSessionState<T>(key: string, initial: T): [T, (next: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = window.sessionStorage.getItem(key);
      return raw === null ? initial : (JSON.parse(raw) as T);
    } catch {
      return initial;
    }
  });
  const set = useCallback((next: T | ((prev: T) => T)) => {
    setValue((prev) => {
      const v = typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
      try { window.sessionStorage.setItem(key, JSON.stringify(v)); } catch { /* best-effort */ }
      return v;
    });
  }, [key]);
  return [value, set];
}
